import { userId } from '@state/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/solid-query';
import { createMemo, createSignal } from 'solid-js';
import type { CommentTreeItem, CommentsAdapter } from './types';

export function useComments() {
  const queryClient = useQueryClient();

  // Dynamic context for the generic service
  const [entityId, setEntityId] = createSignal<string | null>(null);
  const [adapter, setAdapter] = createSignal<CommentsAdapter | null>(null);
  const [activeReplyId, setActiveReplyId] = createSignal<string | null>(null);

  // Called by the UI component to bind this service to a specific feature
  const setContext = (id: string, newAdapter: CommentsAdapter) => {
    setEntityId(id);
    setAdapter(() => newAdapter);
  };

  const invalidate = () => {
    const id = entityId();
    const currentAdapter = adapter();
    if (id && currentAdapter) {
      queryClient.invalidateQueries({ queryKey: [currentAdapter.queryKey, id] });
    }
  };

  const query = useQuery(() => ({
    queryKey: adapter() && entityId() ? [adapter()!.queryKey, entityId()] : ['comments_fallback'],
    queryFn: async () => {
      const currentAdapter = adapter();
      const id = entityId();
      if (!currentAdapter || !id) return [];
      return currentAdapter.getComments(id);
    },
    enabled: !!entityId() && !!adapter(),
  }));

  const postMutation = useMutation(() => ({
    mutationFn: async (params: { content: string; parentId?: string }) => {
      const currentAdapter = adapter();
      const id = entityId();
      const uid = userId();

      if (!currentAdapter || !id || !uid) throw new Error('Missing context for posting comment');

      return currentAdapter.createComment({
        entityId: id,
        profileId: uid,
        content: params.content,
        parentId: params.parentId,
      });
    },
    onSuccess: () => {
      invalidate();
      setActiveReplyId(null);
    },
  }));

  const editMutation = useMutation(() => ({
    mutationFn: async ({ id, content }: { id: string; content: string }) => {
      const currentAdapter = adapter();
      if (!currentAdapter) throw new Error('No adapter set');
      return currentAdapter.updateComment(id, content);
    },
    onSuccess: invalidate,
  }));

  const deleteMutation = useMutation(() => ({
    mutationFn: async (id: string) => {
      const currentAdapter = adapter();
      if (!currentAdapter) throw new Error('No adapter set');
      return currentAdapter.deleteComment(id);
    },
    onSuccess: invalidate,
  }));

  const nestedComments = createMemo<CommentTreeItem[]>(() => {
    const raw = query.data || [];
    const commentMap: Record<string, CommentTreeItem> = {};

    for (const item of raw) {
      commentMap[item.id] = { ...item, replies: [] } as CommentTreeItem;
    }

    const roots: CommentTreeItem[] = [];

    for (const item of Object.values(commentMap)) {
      if (item.parent_id && commentMap[item.parent_id]) {
        commentMap[item.parent_id].replies.push(item);
      } else if (!item.parent_id) {
        roots.push(item);
      }
    }

    return roots;
  });

  return {
    entityId,
    adapter,
    setContext,
    commentsQuery: query,
    postMutation,
    editMutation,
    deleteMutation,
    comments: nestedComments,
    activeReplyId,
    openReply: (id: string) => setActiveReplyId(id),
    closeReply: () => setActiveReplyId(null),
  };
}
